// Connect page — iter101 rebuild (was "Scan").
//
// Layout (top → bottom):
//   1. Header — "Connect" / "Conectar" (Playfair 26px) + back arrow.
//   2. Scanner card — Playfair 17px title + sub + gold "Tap to Start Scanning"
//      CTA.  Whole card is tappable; tapping starts the camera.
//   3. Live camera (only when active).
//   4. Show My QR — gold-outline full-width button (unchanged).
//   5. MORE divider.
//   6. 2×2 grid of "section boxes" — each with TWO tappable zones:
//        Top zone   → navigates to the dedicated section page.
//        Bottom zone → triggers the action (add / scan / discover).
//      Each zone honours the 48px minimum touch target.
//
// Counts are sourced live:
//   • Friends      → /api/friends.accepted.length (0 for guests).
//   • My Places    → /api/custom-locations (logged-in)
//                    or guestStore.getCustomLocations() (guests).
//   • Vendors      → /api/favorites filtered to vendor (logged-in)
//                    or guestStore.getSaved() filtered (guests).
//   • Favorites    → /api/favorites filtered to business (logged-in)
//                    or guestStore.getSaved() filtered (guests).
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, AlertTriangle, X, ScanLine, QrCode, Users, MapPin, Heart, Compass,
} from "lucide-react";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { api, getMe } from "../lib/api";
import { BottomNav } from "../components/BottomNav";
import HeaderActions from "../components/HeaderActions";
import SaveThisSpotSheet from "../components/SaveThisSpotSheet";
import { usePermissionPrimer } from "../components/PermissionPrimer";
import { useI18n } from "../lib/i18n";
import * as guestStore from "../lib/guestStorage";

const REGION_ID = "habano-qr-region";

export default function Connect() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { ask: askPermission, PrimerNode } = usePermissionPrimer();
  const [active, setActive] = useState(false);
  const [error, setError] = useState(null);
  const scanner = useRef(null);
  const lastSent = useRef(0);
  const [counts, setCounts] = useState({ friends: 0, places: 0, vendors: 0, favorites: 0 });
  const [user, setUser] = useState(null);
  const [spotSheetOpen, setSpotSheetOpen] = useState(false);
  const [spotCoords, setSpotCoords] = useState(null);
  const [findingSpot, setFindingSpot] = useState(false);

  const openSaveSpot = () => {
    if (findingSpot) return;
    if (!navigator.geolocation) { toast.error(t("save_spot_err_perm")); return; }
    setFindingSpot(true);
    const tid = setTimeout(() => {
      setFindingSpot(false);
      toast.error(t("save_spot_err_timeout"));
    }, 8000);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(tid);
        setFindingSpot(false);
        setSpotCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setSpotSheetOpen(true);
      },
      () => {
        clearTimeout(tid);
        setFindingSpot(false);
        toast.error(t("save_spot_err_perm"));
      },
      { enableHighAccuracy: true, timeout: 7500, maximumAge: 60000 },
    );
  };

  // Pull counts for the four section boxes. Single effect — runs once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await getMe();
      if (cancelled) return;
      setUser(me);
      if (!me) {
        const saved = guestStore.getSaved();
        const customs = guestStore.getCustomLocations();
        setCounts({
          friends: 0,
          places: customs.length,
          vendors: saved.filter((s) => s.target_type === "vendor").length,
          favorites: saved.filter((s) => s.target_type === "business").length,
        });
        return;
      }
      const [friendsRes, customsRes, favsRes] = await Promise.all([
        api.get("/friends").catch(() => ({ data: { accepted: [] } })),
        api.get("/custom-locations").catch(() => ({ data: [] })),
        api.get("/favorites").catch(() => ({ data: [] })),
      ]);
      if (cancelled) return;
      const favs = favsRes.data || [];
      setCounts({
        friends: (friendsRes.data?.accepted || []).length,
        places: (customsRes.data || []).length,
        vendors: favs.filter((f) => f.target_type === "vendor").length,
        favorites: favs.filter((f) => f.target_type === "business").length,
      });
    })();
    return () => { cancelled = true; };
  }, []);

  // Scanner — unchanged behaviour from previous Scan page.
  const stop = async () => {
    if (scanner.current && scanner.current.isScanning) {
      try { await scanner.current.stop(); } catch { /* ignore */ }
    }
    setActive(false);
  };
  const submit = async (raw) => {
    if (!raw) return;
    const now = Date.now();
    if (now - lastSent.current < 800) return;
    lastSent.current = now;
    try {
      const { data } = await api.post("/qr/scan", { raw_data: raw });
      if (data?.type === "onboarding" && data?.redirect) { await stop(); navigate(data.redirect); return; }
      if (data?.ok && data?.type === "business_navigate" && data?.business_id) {
        toast.success(data.message); await stop(); navigate(`/places/${data.business_id}`); return;
      }
      if (data?.ok && data?.type === "friend" && data?.user_id) {
        toast.success(data.message); await stop(); navigate(`/friends/${data.user_id}`); return;
      }
      if (data?.ok === false && data?.type === "rate_limited") toast.error(data.message);
      else if (data?.ok === false) toast.warning(data.message);
      else toast.success(data.message);
      await stop();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };
  const start = () => {
    askPermission("camera", async () => {
      setError(null);
      // iter114 — The Html5Qrcode constructor requires the target DOM
      // element to exist at instantiation time. Our camera region only
      // mounts when `active === true`, so flip state FIRST, yield a
      // microtask for React to commit, THEN bind the scanner. Matches
      // the working pattern in FriendScanModal.jsx.
      setActive(true);
      try {
        await new Promise((r) => setTimeout(r, 50));
        if (!scanner.current) scanner.current = new Html5Qrcode(REGION_ID);
        await scanner.current.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          (decoded) => { submit(decoded); },
          () => {},
        );
      } catch (e) {
        setError(e?.message || "Could not access camera. Allow camera permission and try again.");
        setActive(false);
      }
    });
  };
  useEffect(() => () => { stop(); }, []);

  return (
    <main data-testid="connect-page" className="min-h-screen pb-32" style={{ background: "#0B0B0B" }}>
      {PrimerNode}
      <header className="px-5 pt-6 pb-3 flex items-center gap-3">
        <Link to="/" data-testid="connect-back" aria-label="Back"
          className="grid place-items-center"
          style={{ minHeight: 48, minWidth: 48, marginLeft: -8 }}>
          <ArrowLeft className="w-5 h-5 text-foreground/80" strokeWidth={1.5} />
        </Link>
        <h1
          data-testid="connect-page-title"
          className="flex-1 min-w-0"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: "#F5F1E8" }}
        >
          {t("connect_page_title")}
        </h1>
        <HeaderActions user={user} testidPrefix="connect" />
      </header>

      {active && (
        <section className="mx-5 mt-4 rounded-[20px] overflow-hidden relative aspect-square"
          data-testid="connect-camera-stage" style={{ background: "#000" }}>
          <div id={REGION_ID} className="w-full h-full [&_video]:object-cover [&_video]:w-full [&_video]:h-full" />
          <div className="pointer-events-none absolute inset-12 border-2 border-gold rounded-[14px]" />
          <button onClick={stop} data-testid="connect-scan-stop" aria-label="Stop"
            className="absolute top-3 right-3 grid place-items-center text-foreground"
            style={{ width: 48, height: 48, borderRadius: 999, background: "rgba(0,0,0,0.55)",
              backdropFilter: "blur(8px)", border: "1px solid rgba(232,184,75,0.4)" }}>
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </section>
      )}

      {!active && (
        <button
          type="button"
          data-testid="connect-scan-card"
          onClick={start}
          className="mx-5 mt-4 block w-[calc(100%-2.5rem)] text-center transition-transform duration-200 active:scale-[0.99]"
          style={{
            background: "#141414",
            border: "1px solid rgba(232,184,75,0.35)",
            borderRadius: 18,
            padding: "26px 24px",
            backgroundImage:
              "radial-gradient(ellipse at center, rgba(232,184,75,0.05) 0%, transparent 70%)",
            minHeight: 48,
          }}
        >
          <ScanLine style={{ width: 36, height: 36, color: "#E8B84B", margin: "0 auto" }} strokeWidth={1.5} />
          <div
            data-testid="connect-scan-title"
            className="mt-3"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, color: "#F5F1E8", lineHeight: 1.2 }}
          >
            {t("connect_scan_title")}
          </div>
          <p
            className="mt-1.5 mx-auto max-w-xs"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#9A9490", lineHeight: 1.4 }}
          >
            {t("connect_scan_sub")}
          </p>
          <span
            data-testid="connect-scan-cta"
            role="presentation"
            className="inline-flex items-center gap-2 mt-4"
            style={{
              background: "linear-gradient(180deg, #F5CC55, #E8B84B)",
              color: "#0B0B0B",
              borderRadius: 10,
              padding: "9px 20px",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <ScanLine className="w-4 h-4" strokeWidth={2} />
            {t("connect_scan_cta")}
          </span>
        </button>
      )}

      <div className="px-5 mt-5">
        {/* Show My QR */}
        <Link
          to="/my-qr"
          data-testid="connect-show-my-qr"
          className="w-full inline-flex items-center justify-center gap-2"
          style={{ height: 48, background: "transparent", border: "1px solid rgba(232,184,75,0.35)",
            borderRadius: 12, color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 500 }}
        >
          <QrCode style={{ width: 18, height: 18 }} strokeWidth={1.5} /> {t("connect_show_my_qr")}
        </Link>

        {/* MORE divider */}
        <div data-testid="connect-divider-more" className="my-4 flex items-center gap-3" aria-hidden>
          <span className="flex-1 h-px" style={{ background: "#6a6a6a" }} />
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, letterSpacing: "1.5px", color: "#6a6a6a" }}>
            {t("connect_more")}
          </span>
          <span className="flex-1 h-px" style={{ background: "#6a6a6a" }} />
        </div>

        {/* 2×2 grid */}
        <div data-testid="connect-grid" className="grid grid-cols-2 gap-3">
          <SectionBox
            testid="connect-box-friends"
            icon={Users}
            count={counts.friends}
            label={t("connect_box_friends_label")}
            tapHint={t("connect_tap_to_view")}
            topTo="/friends"
            bottomActionTestid="connect-box-friends-action"
            bottomActionLabel={t("connect_box_friends_action")}
            bottomActionTone="gold"
            onBottomAction={start}
          />
          <SectionBox
            testid="connect-box-places"
            icon={MapPin}
            count={counts.places}
            label={t("connect_box_places_label")}
            tapHint={t("connect_tap_to_view")}
            topTo="/my-places/custom"
            bottomActionTestid="connect-box-places-action"
            bottomActionLabel={t("connect_box_places_action")}
            bottomActionTone="gold"
            onBottomAction={openSaveSpot}
          />
          <SectionBox
            testid="connect-box-vendors"
            icon={Users}
            count={counts.vendors}
            label={t("connect_box_vendors_label")}
            tapHint={t("connect_tap_to_view")}
            topTo="/my-vendors"
            bottomActionTestid="connect-box-vendors-action"
            bottomActionLabel={t("connect_box_vendors_action")}
            bottomActionIcon={QrCode}
            bottomActionTone="green"
            onBottomAction={start}
          />
          <SectionBox
            testid="connect-box-favorites"
            icon={Heart}
            count={counts.favorites}
            label={t("connect_box_favorites_label")}
            tapHint={t("connect_tap_to_view")}
            topTo="/my-favorites"
            bottomActionTestid="connect-box-favorites-action"
            bottomActionLabel={t("connect_box_favorites_action")}
            bottomActionIcon={Compass}
            bottomActionTone="gold"
            bottomActionTo="/"
          />
        </div>

        {error && (
          <div className="mt-3 text-xs text-destructive flex items-center gap-2" data-testid="connect-error">
            <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} /> {error}
          </div>
        )}
      </div>

      <BottomNav />

      {/* iter117 — Save This Spot sheet (shared with Journey) */}
      <SaveThisSpotSheet
        open={spotSheetOpen}
        onClose={() => setSpotSheetOpen(false)}
        coords={spotCoords}
      />
    </main>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// SectionBox — 2-zone tappable card used inside the 2×2 grid.
// ──────────────────────────────────────────────────────────────────────────
function SectionBox({
  testid,
  icon: Icon,
  count,
  label,
  tapHint,
  topTo,
  bottomActionTestid,
  bottomActionLabel,
  bottomActionIcon,
  bottomActionTone = "gold",
  bottomActionTo,
  onBottomAction,
}) {
  const greenColor = "#25D366";
  const bottomBg = bottomActionTone === "green" ? "rgba(37,211,102,0.05)" : "rgba(232,184,75,0.05)";
  const bottomFg = bottomActionTone === "green" ? greenColor : "#E8B84B";
  const ActionIcon = bottomActionIcon;
  return (
    <div
      data-testid={testid}
      style={{
        background: "#141414",
        border: "1px solid rgba(232,184,75,0.25)",
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: "0 0 8px rgba(232,184,75,0.06)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Link
        to={topTo}
        data-testid={`${testid}-top`}
        className="block text-center transition-colors hover:bg-white/[0.02]"
        style={{
          padding: "14px 8px 10px",
          borderBottom: "0.5px solid #1f1f1f",
          minHeight: 48,
        }}
      >
        <Icon style={{ width: 22, height: 22, color: "#E8B84B", margin: "0 auto" }} strokeWidth={1.5} />
        <div
          data-testid={`${testid}-count`}
          style={{ fontFamily: "'Playfair Display', serif", fontSize: 34, color: "#E8B84B", lineHeight: 1.1, marginTop: 2 }}
        >
          {count}
        </div>
        <div
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 9,
            color: "#B0AAA3",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            marginTop: 4,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 9,
            color: "#6a6a6a",
            marginTop: 3,
          }}
        >
          {tapHint}
        </div>
      </Link>
      <BottomZone
        testid={bottomActionTestid}
        label={bottomActionLabel}
        Icon={ActionIcon}
        background={bottomBg}
        color={bottomFg}
        to={bottomActionTo}
        onClick={onBottomAction}
      />
    </div>
  );
}

function BottomZone({ testid, label, Icon, background, color, to, onClick }) {
  const content = (
    <span
      className="inline-flex items-center justify-center gap-1.5"
      style={{
        width: "100%",
        minHeight: 48,
        padding: 8,
        background,
        color,
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 10,
        fontWeight: 500,
      }}
    >
      {Icon && <Icon style={{ width: 13, height: 13 }} strokeWidth={2} />}
      {label}
    </span>
  );
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testid}
        className="block transition-opacity hover:opacity-90"
      >
        {content}
      </Link>
    );
  }
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="block w-full text-left transition-opacity hover:opacity-90"
      style={{ background: "transparent", border: "none", padding: 0 }}
    >
      {content}
    </button>
  );
}
